import { useState, useEffect, useRef, useCallback } from "react";

const isDevelopmentMode = false;
const msInSec = isDevelopmentMode ? 10 : 1000;

export const useTimer = (initialSeconds) => {
  const [totalSeconds, setTotalSeconds] = useState(initialSeconds);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  useEffect(() => {
    setTotalSeconds(initialSeconds); // reset timer if initial time changes
    setElapsedSeconds(0);
  }, [initialSeconds]);

  const remainingSeconds = totalSeconds - elapsedSeconds;

  const lastTickTime = useRef(Date.now());

  const [isPaused, setIsPaused] = useState(true);
  const isPausedRef = useRef(isPaused);
  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (isPausedRef.current) {
        return;
      }

      const now = Date.now();
      const msSinceLastTick = now - lastTickTime.current;
      const secsSinceLastTick = Math.floor(msSinceLastTick / msInSec);

      if (secsSinceLastTick > 0) {
        setElapsedSeconds((prev) => prev + secsSinceLastTick);
        lastTickTime.current = now;
      }
    }, 10);

    return () => clearInterval(interval);
  }, []);

  const resetTimer = useCallback(() => {
    setElapsedSeconds(0);
    lastTickTime.current = Date.now();
  }, []);

  const pauseTimer = useCallback(() => {
    setIsPaused(true);
  }, []);

  const resumeTimer = useCallback(() => {
    lastTickTime.current = Date.now();
    setIsPaused(false);
  }, []);

  return {
    remainingSeconds: remainingSeconds < 0 ? 0 : remainingSeconds,
    elapsedSeconds,
    totalSeconds,
    isFinished: remainingSeconds <= 0,
    isPaused,
    resetTimer,
    pauseTimer,
    resumeTimer,
  };
};
